import {useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  getHotelIdsToDisplay,
  getSearchId,
  getSimilarHotelsMapScope
} from 'modules/sapiSearch/selectors'
import {
  getSimilarHotelsInput,
  getSimilarHotelsPricingContext
} from 'modules/sapiSearch/similarHotelsSelectors'
import {setSimilarHotelsMapResults} from 'modules/sapiSearch/slice'
import {equals} from 'ramda'
import {
  getSimilarHotelCheapestOffers,
  getSimilarHotelDisplayPrices
} from 'utils/similarHotels'

import {isAirbnbOffer} from '@vio/core/src/offer/business/isAirbnbOffer'

import {MIN_SIMILAR_HOTELS, SIMILAR_HOTELS_TIMEOUT_MS} from './constants'
import {getSimilarHotelBadges} from './utils/badge'
import {getSimilarHotelPerks} from './utils/perks'
import {rankSimilarHotels} from './utils/ranking'

/** Rank main-search alternatives after offers finish, within the carousel deadline. */
export const useSimilarHotels = () => {
  const dispatch = useDispatch()
  const visibleHotelIds = useSelector(getHotelIdsToDisplay)
  const mapScope = useSelector(getSimilarHotelsMapScope)
  const currentInput = useSelector(getSimilarHotelsInput)
  const pricingContext = useSelector(getSimilarHotelsPricingContext)
  const searchId = useSelector(getSearchId)
  const [expiredSearchId, setExpiredSearchId] = useState<string>()
  const timedOut = Boolean(searchId && expiredSearchId === searchId)
  const [completedSearch, setCompletedSearch] = useState<{
    searchId: string
    input: ReturnType<typeof getSimilarHotelsInput>
  }>()
  const input =
    completedSearch && completedSearch.searchId === searchId
      ? completedSearch.input
      : currentInput

  // Pagination changes search readiness and adds candidates after the carousel settles.
  useEffect(() => {
    if (
      searchId &&
      completedSearch?.searchId !== searchId &&
      currentInput.anchorOffersReady &&
      !timedOut
    ) {
      setCompletedSearch({searchId, input: currentInput})
    }
  }, [searchId, currentInput, completedSearch, timedOut])

  useEffect(() => {
    if (!searchId || input.anchorOffersReady || timedOut) return
    const timer = setTimeout(
      () => setExpiredSearchId(searchId),
      SIMILAR_HOTELS_TIMEOUT_MS
    )
    return () => clearTimeout(timer)
  }, [searchId, input.anchorOffersReady, timedOut])

  const priced = useMemo(() => {
    const cheapestOffers = getSimilarHotelCheapestOffers(
      input.offers,
      pricingContext
    )
    return {
      ...input,
      cheapestOffers,
      displayPrices: getSimilarHotelDisplayPrices(
        input.offers,
        pricingContext,
        cheapestOffers
      )
    }
  }, [input, pricingContext])

  const ranking = useMemo(
    () =>
      rankSimilarHotels({
        ...priced,
        hotels: Object.fromEntries(
          Object.entries(priced.hotels).filter(
            ([id]) =>
              id === input.anchorId ||
              (id !== input.firstResultId &&
                !priced.offers[id]?.offers.some(isAirbnbOffer))
          )
        )
      }),
    [priced, input.anchorId, input.firstResultId]
  )

  const isLoading = !timedOut && !input.anchorOffersReady
  const anchorAvailable = Boolean(priced.displayPrices[input.anchorId])
  const recommendations = useMemo(() => {
    if (
      isLoading ||
      timedOut ||
      ranking.recommendations.length < MIN_SIMILAR_HOTELS
    )
      return []

    const candidates = ranking.recommendations
    const anchor = priced.hotels[priced.anchorId]
    const anchorOffers = priced.cheapestOffers[priced.anchorId]?.offers
    const badges = getSimilarHotelBadges(candidates, anchor)

    return candidates.map(item => {
      const {hotel, priceDifference} = item
      const entity = priced.offers[hotel.objectID]
      const selectedOffers = priced.cheapestOffers[hotel.objectID]?.offers
      const selectedOffer = selectedOffers?.[0]
      const bundle = entity?.splitBooking
      const isSelectedBundle =
        bundle?.url && selectedOffer === bundle.offers?.[0]?.offer

      return {
        ...item,
        badge: badges[hotel.objectID],
        providerOffers: selectedOffers,
        offer: entity?.offers.find(offer => offer === selectedOffer),
        splitBookingOfferUrl: isSelectedBundle ? bundle.url : undefined,
        anchorPriceTotal: entity?.anchorPriceRateBreakdown?.totalRate ?? 0,
        perks: getSimilarHotelPerks({
          anchor,
          hotel,
          priceDifference,
          anchorOffers,
          offers: selectedOffers
        })
      }
    })
  }, [ranking, isLoading, timedOut, priced])

  const lastMapResults =
    useRef<Parameters<typeof setSimilarHotelsMapResults>[0]>()

  useEffect(() => {
    const additions = recommendations.filter(
      ({hotel, distanceKm}) =>
        !visibleHotelIds.includes(hotel.objectID) && distanceKm !== undefined
    )
    const results =
      mapScope && additions.length
        ? {
            scope: mapScope,
            searchId,
            hotelIds: additions.map(({hotel}) => hotel.objectID)
          }
        : undefined

    // Publishing updates Redux; equivalent data must not trigger another publication.
    if (equals(lastMapResults.current, results)) return
    lastMapResults.current = results
    dispatch(setSimilarHotelsMapResults(results))
  }, [dispatch, recommendations, visibleHotelIds, mapScope, searchId])

  useEffect(
    () => () => {
      dispatch(setSimilarHotelsMapResults(undefined))
    },
    [dispatch]
  )

  return {recommendations, isLoading, anchorAvailable}
}
